import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { authService } from '../services/api/auth.service';
import { resolverFotoUsuario } from '../services/api/archivos.service';
import { storageService } from '../utils/storage';
import type { Usuario, LoginResponse, RegisterRequest } from '../modelos/Auth';

interface AuthContextType {
  user: Usuario | null;
  isAuthenticated: boolean;
  esCargando: boolean;
  error: string | null;
  esBloqueado: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  demoLogin: () => Promise<LoginResponse>;
  register: (data: RegisterRequest) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  clearError: () => void;
  checkAuth: () => Promise<boolean>;
  updateUser: (campos: Partial<Usuario>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<Usuario | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [esCargando, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [esBloqueado, setEsBloqueado] = useState(false);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    try {
      const isValid = await authService.validateSession();
      
      if (isValid) {
        const currentUser = storageService.getUser();
        setUser(currentUser);
        setIsAuthenticated(true);
        if (currentUser?.id) {
          resolverFotoUsuario(currentUser.id).then(({ url }) => {
            if (url) updateUser({ fotoUrl: url });
          });
        }
        return true;
      } else {
        setUser(null);
        setIsAuthenticated(false);
        return false;
      }
    } catch {
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string): Promise<LoginResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.login(email, password);

      const userData: Usuario = {
        id: response.id,
        nombre: response.nombre,
        apellido: response.apellido,
        email: response.email,
        esActivo: true,
        requiereCambioPassword: response.requiereCambioPassword,
        esOnboarding: response.esOnboarding,
        esCreadoPorApiKey: response.esCreadoPorApiKey,
        esSuperAdminSenciyo: response.esSuperAdminSenciyo,
        esCuentaDemo: response.esCuentaDemo,
      };

      setUser(userData);
      setIsAuthenticated(true);
      resolverFotoUsuario(userData.id).then(({ url }) => {
        if (url) updateUser({ fotoUrl: url });
      });

      return response;
    } catch (err: unknown) {
      const status = (err as { status?: number }).status;
      const baseMessage = err instanceof Error ? err.message : 'Error al iniciar sesión';
      if (status === 429) {
        setEsBloqueado(true);
        setError(baseMessage);
      } else {
        setEsBloqueado(false);
        setError(`${baseMessage} ¡Inténtalo de nuevo!`);
      }
      throw new Error(baseMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const demoLogin = async (): Promise<LoginResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.demoLogin();

      const userData: Usuario = {
        id: response.id,
        nombre: response.nombre,
        apellido: response.apellido,
        email: response.email,
        esActivo: true,
        requiereCambioPassword: response.requiereCambioPassword,
        esOnboarding: response.esOnboarding,
        esCreadoPorApiKey: response.esCreadoPorApiKey,
        esSuperAdminSenciyo: response.esSuperAdminSenciyo,
        esCuentaDemo: response.esCuentaDemo,
      };

      setUser(userData);
      setIsAuthenticated(true);
      resolverFotoUsuario(userData.id).then(({ url }) => {
        if (url) updateUser({ fotoUrl: url });
      });

      return response;
    } catch (err: unknown) {
      const baseMessage = err instanceof Error ? err.message : 'No se pudo iniciar el acceso Demo';
      setError(baseMessage);
      throw new Error(baseMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (data: RegisterRequest): Promise<LoginResponse> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.register(data);

      const userData: Usuario = {
        id: response.id,
        nombre: response.nombre,
        apellido: response.apellido,
        email: response.email,
        esActivo: true,
        requiereCambioPassword: response.requiereCambioPassword,
        esOnboarding: response.esOnboarding,
        esCreadoPorApiKey: response.esCreadoPorApiKey,
        esSuperAdminSenciyo: response.esSuperAdminSenciyo,
        esCuentaDemo: response.esCuentaDemo,
      };

      setUser(userData);
      setIsAuthenticated(true);

      return response;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear la cuenta';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const refreshed = await authService.refreshToken();
      if (refreshed) {
        const currentUser = storageService.getUser();
        setUser(currentUser);
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setEsBloqueado(false);
  }, []);

  useEffect(() => {
    if (user?.email) {
      window.rybbit?.identify(user.email);
    }
  }, [user?.email]);

  const updateUser = useCallback((campos: Partial<Usuario>) => {
    storageService.updateUser(campos);
    setUser(prev => (prev ? { ...prev, ...campos } : prev));
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    esCargando,
    error,
    esBloqueado,
    login,
    demoLogin,
    register,
    logout,
    refreshSession,
    clearError,
    checkAuth,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
